import { effect, untracked, type DestroyRef, type Signal } from '@angular/core';
import { createHttpContextFromOptions } from '@quipex/core/primitives';
import type {
    JobProgressMessage,
    RealtimeConnectionState,
} from '@quipex/core/realtime';
import type {
    SelectedDocumentExportAccepted,
    SelectedDocumentExportAcceptance,
} from '@quipex/feature/building-context';
import type {
    BuildingDocumentsApiService,
    DocumentExportStatusResponse,
} from '@quipex/shared/contracts';
import { firstValueFrom } from 'rxjs';
import {
    activityIdentitiesEqual,
    type ActivityIdentity,
    type ActivityItem,
    type ActivitySession,
    type ActivityKey,
} from '../activity-journal';
import { mapActivityJob } from '../activity-journal/mapper';
import { parseActivityMessage } from '../activity-journal/message';
import type { reduceActivity } from '../activity-journal/reducer';
import type { ActivityToastDecisionSnapshot } from './action-contract';
import { observeActivityJobUpdate } from './job-observer';
import { selectedExportStatusTime } from './selected-export-status-time';

export class SelectedExportRecovery {
    private readonly selectedSnapshots = new Map<string, string>();
    public readonly selectedReads = new Map<
        string,
        { pending: boolean; dirty: boolean; attempts: number }
    >();
    constructor(
        private readonly owner: {
            identity: () => ActivityIdentity | null;
            generation: () => number;
            items: () => readonly ActivityItem[];
            session: () => ActivitySession;
            apply: (action: Parameters<typeof reduceActivity>[1]) => void;
            markUnavailable: (key: ActivityKey) => void;
            markStale: (key: ActivityKey) => void;
            api: () => BuildingDocumentsApiService;
            save: (session: ActivitySession) => void;
            toast: (decision: ActivityToastDecisionSnapshot) => void;
        }
    ) {}
    public observeRealtime(message: JobProgressMessage): boolean {
        if (
            message.jobType !== 'SelectedBuildingDocumentExport' ||
            !message.buildingId
        )
            return false;
        if (
            !this.owner
                .items()
                .some((item) =>
                    item.jobs.some((job) => job.jobId === message.jobId)
                )
        ) {
            const seed = mapActivityJob({
                ...message,
                status: 'Queued',
                message: 'Preparing selected documents.',
            });
            if (seed) this.owner.apply({ type: 'job-observed', update: seed });
        }
        void this.refreshSelectedJob(
            message.buildingId,
            message.jobId,
            true,
            message.status === 'Completed'
                ? (parseActivityMessage(message.message) ?? null)
                : null
        );
        return true;
    }
    public clear(): void {
        this.selectedReads.clear();
        this.selectedSnapshots.clear();
    }
    public capture(): SelectedDocumentExportAcceptance | null {
        const identity = this.owner.identity();
        const generation = this.owner.generation();
        if (!identity) return null;
        const isCurrent = () =>
            this.owner.generation() === generation &&
            activityIdentitiesEqual(this.owner.identity(), identity);
        const accept = (
            buildingId: string,
            accepted: SelectedDocumentExportAccepted,
            selectionKey: string
        ) => {
            if (
                this.owner.generation() !== generation ||
                !activityIdentitiesEqual(this.owner.identity(), identity)
            )
                return false;
            const existingSnapshot =
                this.selectedSnapshots.get(accepted.jobId) ??
                this.owner
                    .items()
                    .flatMap((item) => item.jobs)
                    .find((job) => job.jobId === accepted.jobId)
                    ?.selectedSelectionKey;
            if (existingSnapshot && existingSnapshot !== selectionKey)
                return false;
            this.selectedSnapshots.set(accepted.jobId, selectionKey);
            this.owner.apply({
                type: 'job-observed',
                update: {
                    key: `job:${accepted.jobId}`,
                    correlationId: null,
                    jobId: accepted.jobId,
                    jobType: 'SelectedBuildingDocumentExport',
                    stage: 'export',
                    status: 'Queued',
                    warning: false,
                    percentComplete: null,
                    message: 'Preparing selected documents.',
                    occurredAtUtc: accepted.createdAt,
                    documentPublicId: null,
                    buildingPublicId: buildingId,
                    packId: null,
                    exportKind: 'selected-documents',
                    selectedSelectionKey: selectionKey,
                    downloadAvailable: false,
                    expiresAt: accepted.expiresAt,
                },
            });
            void this.refreshSelectedJob(buildingId, accepted.jobId, true);
            return true;
        };
        return Object.assign(accept, { isCurrent });
    }

    public refreshSelectedJobs(): void {
        for (const item of this.owner.items())
            for (const job of item.jobs) {
                if (
                    job.jobType !== 'SelectedBuildingDocumentExport' ||
                    !job.buildingPublicId ||
                    item.identityQuarantined
                )
                    continue;
                if (job.expiresAt && Date.parse(job.expiresAt) <= Date.now()) {
                    this.owner.markUnavailable(item.key);
                    continue;
                }
                if (
                    item.availability === 'stale' ||
                    !['Completed', 'Failed', 'Expired'].includes(job.status)
                )
                    void this.refreshSelectedJob(
                        job.buildingPublicId,
                        job.jobId
                    );
            }
    }

    public async refreshSelectedJob(
        buildingId: string,
        jobId: string,
        triggered = false,
        completionAdvisory: string | null = null
    ): Promise<void> {
        const generation = this.owner.generation();
        let read = this.selectedReads.get(jobId);
        if (!read) {
            read = { pending: false, dirty: false, attempts: 0 };
            this.selectedReads.set(jobId, read);
        }
        if (triggered) read.attempts = 0;
        if (read.pending) {
            read.dirty = true;
            return;
        }
        if (!this.owner.identity()) return;
        if (read.attempts >= 60) {
            this.owner.markStale(`job:${jobId}`);
            return;
        }
        read.pending = true;
        read.attempts++;
        try {
            const status = await firstValueFrom(
                this.owner
                    .api()
                    .getBuildingDocumentExportById(buildingId, jobId, {
                        context: createHttpContextFromOptions({
                            // Activity selected recovery owns retries and unavailable state; automatic error toasts duplicate that experience.
                            suppressAutoErrorToast: true,
                        }),
                    })
            );
            if (generation !== this.owner.generation()) return;
            this.observeSelectedStatus(
                buildingId,
                jobId,
                generation,
                status,
                completionAdvisory
            );
        } catch {
            if (generation === this.owner.generation())
                this.owner.markStale(`job:${jobId}`);
        } finally {
            read.pending = false;
            if (read.dirty && generation === this.owner.generation()) {
                read.dirty = false;
                void this.refreshSelectedJob(buildingId, jobId);
            }
        }
    }

    private observeSelectedStatus(
        buildingId: string,
        jobId: string,
        generation: number,
        status: DocumentExportStatusResponse,
        completionAdvisory: string | null
    ): void {
        if (
            status.jobId !== jobId ||
            status.jobType !== 'SelectedBuildingDocumentExport'
        ) {
            this.owner.markUnavailable(`job:${jobId}`);
            return;
        }
        const statuses = {
            queued: 'Queued',
            processing: 'Progress',
            completed: 'Completed',
            failed: 'Failed',
            expired: 'Expired',
        } as const;
        const identity = this.owner.identity();
        if (!identity || !Object.hasOwn(statuses, status.status)) return;
        const observation = observeActivityJobUpdate({
            identity,
            activeIdentityVersion: generation,
            session: this.owner.session(),
            update: {
                key: `job:${jobId}`,
                correlationId: null,
                jobId,
                jobType: 'SelectedBuildingDocumentExport',
                stage: 'export',
                status: statuses[status.status],
                warning: false,
                percentComplete: null,
                message:
                    status.status === 'completed'
                        ? (parseActivityMessage(
                              `${completionAdvisory ?? ''} Review the ZIP manifest for any omitted files.`.trim()
                          ) ?? 'Review the ZIP manifest for any omitted files.')
                        : null,
                occurredAtUtc: selectedExportStatusTime(
                    status,
                    this.owner
                        .items()
                        .flatMap((item) => item.jobs)
                        .find((job) => job.jobId === jobId)?.occurredAtUtc
                ),
                documentPublicId: null,
                buildingPublicId: buildingId,
                packId: null,
                exportKind: 'selected-documents',
                downloadAvailable:
                    status.status === 'completed' &&
                    status.downloadAvailable === true,
                expiresAt: status.expiresAt,
            },
        });
        this.owner.save(observation.session);
        this.owner.apply({ type: 'mark-available', key: `job:${jobId}` });
        if (observation.toastDecision)
            this.owner.toast(observation.toastDecision);
    }
}

export function watchSelectedExportRecovery(
    recovery: SelectedExportRecovery,
    connection: Signal<RealtimeConnectionState>,
    destroyRef: DestroyRef,
    document: Document
): void {
    effect(() => {
        if (connection() === 'connected')
            untracked(() => {
                for (const read of recovery.selectedReads.values())
                    read.attempts = 0;
                recovery.refreshSelectedJobs();
            });
    });
    const poll = document.defaultView?.setInterval(
        () => recovery.refreshSelectedJobs(),
        5000
    );
    destroyRef.onDestroy(() => {
        if (poll !== undefined) document.defaultView?.clearInterval(poll);
    });
}
